import { useEffect, useState } from 'react'
import { SyncOutlined } from '@ant-design/icons'
import { Flex, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@/shared/lib/format'

/**
 * "Avtomatik yangilanadi" ko'rsatkichi.
 *
 * <h2>Nega bu kerak edi</h2>
 * Sahifalarda `refetchInterval: 15_000` ALLAQACHON bor edi — ma'lumot
 * haqiqatan har 15 soniyada yangilanardi. Lekin buni ko'rsatadigan hech
 * narsa yo'q edi, ya'ni operator ekranda turgan raqam **hozirgi**mi yoki
 * yarim soat oldingimi — bilmasdi. Bu bo'sh sahifadan yomonroq: eskirgan
 * raqamga qarab qaror qabul qilinadi.
 *
 * Shuning uchun uchta narsa ko'rsatiladi:
 *   · yangilanish davri ("har 15 s") — kutish nima ekani aniq bo'lsin
 *   · oxirgi yangilanish vaqti — raqam qachonlik ekani
 *   · yangilanayotgan payt — aylanuvchi belgi
 *
 * <h2>⚠️ `dataUpdatedAt` — MUVAFFAQIYATLI javob vaqti</h2>
 * TanStack Query buni faqat so'rov o'tganda yangilaydi. Ya'ni tarmoq
 * uzilsa vaqt "qotib" qoladi va bu AYNAN kerakli xatti-harakat: soat
 * yurib turgani ma'lumot yangilanayotganini bildirmaydi. Buning uchun
 * alohida `setInterval` bilan sanoq yuritiladi — soniya sayin qayta
 * chiziladi, lekin manba baribir `dataUpdatedAt`.
 */
interface RefreshIndicatorProps {
  /** `useQuery` dan `dataUpdatedAt` — oxirgi MUVAFFAQIYATLI javob vaqti (ms). */
  updatedAt: number
  /** Hozir so'rov ketyaptimi (`isFetching`). */
  fetching?: boolean
  /**
   * Avtoyangilanish davri, sekundda.
   *
   * ⚠️ Berilmasa — «har N soniyada» VA'DASI umuman chizilmaydi.
   * Avtoyangilanish 2026-08-20 da faqat bosh sahifada qoldirildi;
   * boshqa ekranda bu matn yolg'on bo'lardi va operator eskirgan
   * songa ishonardi.
   */
  intervalSeconds?: number
}

/** "hozirgina" / "12 s oldin" / "3 daq oldin" — qisqa va o'qishga oson. */
function relative(ms: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const sec = Math.max(0, Math.round(ms / 1000))
  if (sec < 5) return t('refresh.justNow')
  if (sec < 60) return t('refresh.secondsAgo', { count: sec })
  return t('refresh.minutesAgo', { count: Math.floor(sec / 60) })
}

export function RefreshIndicator({
  updatedAt,
  fetching = false,
  intervalSeconds,
}: RefreshIndicatorProps) {
  const { t } = useTranslation()

  // Sanoqni yurgizish uchun har soniyada qayta chizamiz. Bu ARZON: bitta
  // matn tuguni, va u faqat shu komponentni qayta chizadi.
  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [])

  if (!updatedAt) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        <SyncOutlined spin={fetching} />{' '}
        {intervalSeconds === undefined
          ? t('refresh.manual')
          : t('refresh.every', { seconds: intervalSeconds })}
      </Typography.Text>
    )
  }

  // ⚠️ `toLocaleTimeString()` EMAS — u BRAUZER zonasi va BRAUZER tilida
  // formatlaydi. Loyihaning qoidasi esa boshqa: ko'rsatiladigan vaqt
  // TENANT zonasida bo'ladi (`shared/lib/format.ts`), chunki Toshkent va
  // Kobul operatorlari aks holda turli soatni o'qishardi.
  //
  // Aynan shu element "bu raqam qanchalik eski?" degan savolga javob
  // beradi — qoidani buzadigan oxirgi joy shu bo'lishi kerak emas edi.
  const exact = formatDateTime(new Date(updatedAt).toISOString())
  return (
    <Tooltip
      title={
        intervalSeconds === undefined
          ? t('refresh.tooltipManual', { time: exact })
          : t('refresh.tooltip', { time: exact, seconds: intervalSeconds })
      }
    >
      <Flex align="center" gap={6}>
        <SyncOutlined spin={fetching} style={{ fontSize: 12, opacity: fetching ? 1 : 0.45 }} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {intervalSeconds === undefined
            ? t('refresh.manual')
            : t('refresh.every', { seconds: intervalSeconds })}{' '}
          · {t('refresh.updated')}{' '}
          {relative(Date.now() - updatedAt, t)}
        </Typography.Text>
      </Flex>
    </Tooltip>
  )
}
